import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Label } from '../ui/label';
import {
  formatDate,
  formatCurrency,
  formatReferenceType,
} from '../../utils/formatters';
import { type GeneralLedger } from '../../types/drawdown';

interface LedgerEntryDetailModalProps {
  entry: GeneralLedger | null;
  onClose: () => void;
}

export function JournalEntryDetailModal({
  entry,
  onClose,
}: LedgerEntryDetailModalProps) {
  if (!entry) return null;

  return (
    <Dialog open={!!entry} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Ledger Entry Details</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Document Date</Label>
              <div>{formatDate(entry.document_date)}</div>
            </div>
            <div>
              <Label>Posting Date</Label>
              <div>{formatDate(entry.posting_date)}</div>
            </div>
            <div>
              <Label>Journal Entry #</Label>
              <div className="font-mono">
                {entry.journal_entry_number ?? '-'}
              </div>
            </div>
            <div>
              <Label>Reference Type</Label>
              <div>{formatReferenceType(entry.reference_type)}</div>
            </div>
            <div>
              <Label>Reference</Label>
              <div className="font-mono">{entry.reference || '-'}</div>
            </div>
            <div>
              <Label>Account</Label>
              <div>
                <div className="font-medium">{entry.account_code}</div>
                <div className="text-sm text-muted-foreground">
                  {entry.account_name}
                </div>
              </div>
            </div>
            <div>
              <Label>Amount</Label>
              <div>
                {entry.debit_amount > 0 && (
                  <div className="text-green-600 font-medium">
                    Debit:{' '}
                    {formatCurrency(entry.debit_amount, {
                      currency: entry.currency,
                    })}
                  </div>
                )}
                {entry.credit_amount > 0 && (
                  <div className="text-red-600 font-medium">
                    Credit:{' '}
                    {formatCurrency(entry.credit_amount, {
                      currency: entry.currency,
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div>
            <Label>Description</Label>
            <div>{entry.description}</div>
          </div>

          {entry.notes && (
            <div>
              <Label>Notes</Label>
              <div className="text-sm text-muted-foreground">{entry.notes}</div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
